import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import {
  BarChart3, MousePointerClick, TrendingUp, Users, Globe as GlobeIcon,
  ArrowLeft, ExternalLink, RefreshCw, Loader2, Search, Download, Lock, UserPlus, Mail, X,
} from 'lucide-react';
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
} from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RANGE_OPTIONS = [
  { label: 'Last 7 days', value: 7 },
  { label: 'Last 30 days', value: 30 },
  { label: 'Last 90 days', value: 90 },
  { label: 'All time', value: 0 },
];

const fmtDate = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  } catch { return iso; }
};

const fmtDateTime = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  } catch { return iso; }
};

const authHeaders = () => {
  const token = localStorage.getItem('admin_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Compact KPI card
const StatCard = ({ icon: Icon, label, value, sub, testId }) => (
  <Card className="border-gray-200" data-testid={testId}>
    <CardContent className="p-5">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs uppercase tracking-wider text-gray-500 font-medium">{label}</p>
          <p className="text-3xl font-bold text-gray-900 mt-1" data-testid={`${testId}-value`}>{value}</p>
          {sub && <p className="text-xs text-gray-500 mt-1">{sub}</p>}
        </div>
        <div className="p-2 rounded-lg bg-[#00457f]/10 text-[#00457f]">
          <Icon className="w-5 h-5" />
        </div>
      </div>
    </CardContent>
  </Card>
);

const RankedTable = ({ title, rows, columns, emptyText, testId }) => (
  <Card className="border-gray-200" data-testid={testId}>
    <CardHeader className="pb-3">
      <CardTitle className="text-base font-semibold text-gray-900">{title}</CardTitle>
    </CardHeader>
    <CardContent className="pt-0">
      {rows.length === 0 ? (
        <p className="text-sm text-gray-400 py-6 text-center">{emptyText}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs uppercase tracking-wider text-gray-500 border-b">
                {columns.map((c) => (
                  <th key={c.key} className={`text-left font-medium py-2 pr-3 ${c.className || ''}`}>{c.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, idx) => (
                <tr key={idx} className="border-b last:border-b-0 hover:bg-gray-50">
                  {columns.map((c) => (
                    <td key={c.key} className={`py-2.5 pr-3 ${c.className || ''}`}>
                      {c.render ? c.render(row, idx) : row[c.key]}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </CardContent>
  </Card>
);

const TimelineChart = ({ data, testId }) => {
  const chartData = useMemo(
    () => (data || []).map((d) => ({ ...d, dateLabel: fmtDate(d.date) })),
    [data]
  );

  return (
    <Card className="border-gray-200" data-testid={testId}>
      <CardHeader className="pb-2">
        <CardTitle className="text-base font-semibold text-gray-900">Click Timeline</CardTitle>
      </CardHeader>
      <CardContent className="pt-2">
        <div className="w-full h-64" style={{ minHeight: 256 }}>
          <ResponsiveContainer width="100%" height="100%" minHeight={256}>
            <AreaChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="clickGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#00457f" stopOpacity={0.6} />
                  <stop offset="100%" stopColor="#00457f" stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
              <XAxis dataKey="dateLabel" tick={{ fontSize: 11, fill: '#6b7280' }} axisLine={false} tickLine={false} interval="preserveStartEnd" />
              <YAxis tick={{ fontSize: 11, fill: '#6b7280' }} axisLine={false} tickLine={false} allowDecimals={false} />
              <Tooltip
                contentStyle={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 12 }}
                labelStyle={{ color: '#111827', fontWeight: 600 }}
              />
              <Area type="monotone" dataKey="clicks" stroke="#00457f" strokeWidth={2} fill="url(#clickGrad)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
};

const CapturesPanel = ({ kind, data, loading, onRefresh, onDownloadCsv, contactLists, bulkListId, setBulkListId, bulkAddToList, bulkBusy, scope }) => {
  const items = data?.items || [];
  const groups = data?.groups || [];
  const total = data?.total || 0;
  const isUnlocks = kind === 'unlocks';
  const emptyText = isUnlocks
    ? 'No unlocks yet. Turn a link into a gated resource and this table will fill up.'
    : 'No orphan newsletter leads. Any signups routed to a contact list live in that list directly.';

  return (
    <div className="space-y-6" data-testid={`captures-panel-${kind}`}>
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3 p-4 bg-white border border-gray-200 rounded-lg">
        <div className="text-sm text-gray-600">
          <span className="font-semibold text-gray-900" data-testid="captures-total">{total}</span> {isUnlocks ? 'unlock' : 'lead'}{total === 1 ? '' : 's'}
          {scope !== 'global' && <> in <span className="font-medium">/{scope}</span></>}
        </div>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <select
            value={bulkListId}
            onChange={(e) => setBulkListId(e.target.value)}
            className="text-sm border border-gray-300 rounded-md px-3 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-[#00457f]"
            data-testid="bulk-list-select"
          >
            <option value="">Add all to list…</option>
            {contactLists.map((l) => (
              <option key={l.id} value={l.id}>{l.name} ({l.contact_count || 0})</option>
            ))}
          </select>
          <Button
            variant="outline"
            size="sm"
            onClick={bulkAddToList}
            disabled={!bulkListId || bulkBusy || total === 0}
            data-testid="bulk-add-btn"
          >
            {bulkBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <UserPlus className="w-4 h-4 mr-2" />}
            Add {total}
          </Button>
          <Button variant="outline" size="sm" onClick={onDownloadCsv} disabled={total === 0} data-testid="download-csv-btn">
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
          <Button variant="outline" size="sm" onClick={onRefresh}>
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Group summary (unlocks only) */}
      {isUnlocks && groups.length > 0 && (
        <Card className="border-gray-200" data-testid="unlocks-groups-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-semibold text-gray-900">By link</CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs uppercase tracking-wider text-gray-500 border-b">
                    <th className="text-left font-medium py-2 pr-3">Link</th>
                    <th className="text-left font-medium py-2 pr-3">Hub</th>
                    <th className="text-right font-medium py-2 pr-3">Captures</th>
                    <th className="text-left font-medium py-2 pr-3 pl-4">Last capture</th>
                  </tr>
                </thead>
                <tbody>
                  {groups.map((g) => (
                    <tr key={g.link_id} className="border-b last:border-b-0 hover:bg-gray-50">
                      <td className="py-2.5 pr-3 font-medium text-gray-900">{g.link_label || g.link_id}</td>
                      <td className="py-2.5 pr-3 text-gray-600">/{g.hub_slug}</td>
                      <td className="py-2.5 pr-3 text-right font-mono font-semibold">{g.count}</td>
                      <td className="py-2.5 pr-3 pl-4 text-gray-600 whitespace-nowrap">{g.last_at ? new Date(g.last_at).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Detail table */}
      <Card className="border-gray-200" data-testid={`captures-table-${kind}`}>
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold text-gray-900">All {isUnlocks ? 'unlocks' : 'leads'}</CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-5 h-5 text-[#00457f] animate-spin" />
            </div>
          ) : items.length === 0 ? (
            <p className="text-sm text-gray-400 py-8 text-center">{emptyText}</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs uppercase tracking-wider text-gray-500 border-b">
                    <th className="text-left font-medium py-2 pr-3">When</th>
                    <th className="text-left font-medium py-2 pr-3">Email</th>
                    <th className="text-left font-medium py-2 pr-3">Name</th>
                    {isUnlocks && <th className="text-left font-medium py-2 pr-3">Link</th>}
                    <th className="text-left font-medium py-2 pr-3">Hub</th>
                    {isUnlocks && <th className="text-left font-medium py-2 pr-3">Resource</th>}
                  </tr>
                </thead>
                <tbody>
                  {items.map((r) => (
                    <tr key={r.id} className="border-b last:border-b-0 hover:bg-gray-50">
                      <td className="py-2.5 pr-3 text-gray-600 whitespace-nowrap">{r.timestamp ? new Date(r.timestamp).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—'}</td>
                      <td className="py-2.5 pr-3 font-medium text-gray-900">{r.email}</td>
                      <td className="py-2.5 pr-3 text-gray-600">{[r.first_name, r.last_name].filter(Boolean).join(' ') || '—'}</td>
                      {isUnlocks && <td className="py-2.5 pr-3 text-gray-600 truncate max-w-[200px]" title={r.link_label}>{r.link_label || r.link_id}</td>}
                      <td className="py-2.5 pr-3 text-gray-600">/{r.hub_slug}</td>
                      {isUnlocks && (
                        <td className="py-2.5 pr-3 text-gray-600 truncate max-w-[220px]" title={r.resource_url}>
                          {r.resource_url ? (
                            <a href={r.resource_url} target="_blank" rel="noopener noreferrer" className="text-[#00457f] hover:underline">
                              {r.resource_url.replace(/^https?:\/\//, '').slice(0, 30)}…
                            </a>
                          ) : '—'}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

const AdminLinkHubAnalytics = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [scope, setScope] = useState(searchParams.get('slug') || 'global'); // 'global' or slug
  const [days, setDays] = useState(30);
  const [hubs, setHubs] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  // Guard: require admin token
  useEffect(() => {
    if (!localStorage.getItem('admin_token')) {
      navigate('/admin/login');
    }
  }, [navigate]);

  // Load list of hubs
  useEffect(() => {
    let ignore = false;
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/link-hub/admin/pages`, { headers: authHeaders() });
        if (!r.ok) return;
        const j = await r.json();
        if (!ignore) setHubs(j || []);
      } catch (e) { /* no-op */ }
    })();
    return () => { ignore = true; };
  }, []);

  // Load analytics
  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    try {
      const path = scope === 'global'
        ? `/api/link-hub/analytics2/global?days=${days}`
        : `/api/link-hub/analytics2/hub/${encodeURIComponent(scope)}?days=${days}`;
      const r = await fetch(`${API_URL}${path}`, { headers: authHeaders() });
      if (!r.ok) {
        toast.error('Failed to load analytics');
        setData(null);
      } else {
        setData(await r.json());
      }
    } catch (e) {
      toast.error('Failed to load analytics');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [scope, days]);

  useEffect(() => { fetchAnalytics(); }, [fetchAnalytics]);

  // ── Tabs: overview | unlocks | leads ───────────────────────────
  const [tab, setTab] = useState('overview');
  const [unlocksData, setUnlocksData] = useState(null);
  const [leadsData, setLeadsData] = useState(null);
  const [captureSearch, setCaptureSearch] = useState('');
  const [captureLoading, setCaptureLoading] = useState(false);
  const [contactLists, setContactLists] = useState([]);
  const [bulkListId, setBulkListId] = useState('');
  const [bulkBusy, setBulkBusy] = useState(false);

  // Load contact lists (for bulk add-to-list dropdown)
  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/email/lists`, { headers: authHeaders() });
        if (r.ok) setContactLists(await r.json());
      } catch { /* no-op */ }
    })();
  }, []);

  const buildCaptureQS = useCallback(() => {
    const p = new URLSearchParams();
    if (scope !== 'global') p.set('slug', scope);
    if (captureSearch.trim()) p.set('search', captureSearch.trim());
    return p.toString();
  }, [scope, captureSearch]);

  const fetchCaptures = useCallback(async () => {
    if (tab === 'overview') return;
    setCaptureLoading(true);
    try {
      const qs = buildCaptureQS();
      const path = tab === 'unlocks' ? 'unlocks' : 'newsletter-leads';
      const r = await fetch(`${API_URL}/api/link-hub/admin/${path}?${qs}`, { headers: authHeaders() });
      if (!r.ok) { toast.error('Failed to load captures'); return; }
      const j = await r.json();
      if (tab === 'unlocks') setUnlocksData(j); else setLeadsData(j);
    } catch { toast.error('Failed to load captures'); }
    finally { setCaptureLoading(false); }
  }, [tab, buildCaptureQS]);

  useEffect(() => { fetchCaptures(); }, [fetchCaptures]);

  const downloadCsv = () => {
    const qs = buildCaptureQS();
    const token = localStorage.getItem('admin_token');
    const path = tab === 'unlocks' ? 'unlocks/export.csv' : 'newsletter-leads/export.csv';
    // Fetch and force download so we can send the Authorization header
    fetch(`${API_URL}/api/link-hub/admin/${path}?${qs}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(async (r) => {
        if (!r.ok) throw new Error('Download failed');
        const blob = await r.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = (r.headers.get('Content-Disposition')?.match(/filename="?([^";]+)"?/)?.[1]) || `${path.split('/')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 1000);
      })
      .catch(() => toast.error('CSV download failed'));
  };

  const bulkAddToList = async () => {
    if (!bulkListId) { toast.error('Pick a contact list first'); return; }
    setBulkBusy(true);
    try {
      const body = { list_id: bulkListId, search: captureSearch.trim() || undefined };
      if (scope !== 'global') body.slug = scope;
      const path = tab === 'unlocks' ? 'unlocks/add-to-list' : 'newsletter-leads/add-to-list';
      const r = await fetch(`${API_URL}/api/link-hub/admin/${path}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify(body),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || 'Bulk add failed');
      toast.success(`Added ${j.added}, skipped ${j.skipped} duplicates. List now has ${j.total_in_list} contacts.`);
    } catch (e) {
      toast.error(e.message || 'Bulk add failed');
    } finally { setBulkBusy(false); }
  };

  const totalClicks = data?.total_clicks || 0;
  const topLinks = data?.top_links || [];
  const topHubs = data?.top_hubs || [];
  const topReferrers = data?.top_referrers || [];
  const recent = data?.recent || [];
  const timeline = data?.timeline || [];

  // Derived stats
  const avgPerDay = useMemo(() => {
    if (!timeline.length) return 0;
    return Math.round(totalClicks / timeline.length);
  }, [totalClicks, timeline.length]);

  const uniqueLinks = topLinks.length;
  const uniqueReferrers = topReferrers.length;

  return (
    <AdminLayout>
      <div className="p-6 max-w-7xl mx-auto" data-testid="link-hub-analytics-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/admin/link-hub')}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              data-testid="back-to-link-hub-btn"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <BarChart3 className="w-6 h-6 text-[#00457f]" />
                Link Hub Analytics
              </h1>
              <p className="text-sm text-gray-500 mt-0.5">Track clicks across every microsite</p>
            </div>
          </div>
          <Button variant="outline" onClick={fetchAnalytics} disabled={loading} data-testid="refresh-analytics-btn">
            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
            Refresh
          </Button>
        </div>

        {/* Tab strip */}
        <div className="flex gap-1 mb-4 border-b border-gray-200" data-testid="analytics-tabs">
          {[
            { key: 'overview', label: 'Overview', icon: BarChart3 },
            { key: 'unlocks', label: 'Unlocks', icon: Lock },
            { key: 'leads', label: 'Newsletter Leads', icon: Mail },
          ].map((t) => {
            const Icon = t.icon;
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={`px-4 py-2.5 text-sm font-medium flex items-center gap-2 border-b-2 -mb-px transition-colors ${
                  active ? 'border-[#00457f] text-[#00457f]' : 'border-transparent text-gray-500 hover:text-gray-800 hover:border-gray-300'
                }`}
                data-testid={`tab-${t.key}`}
              >
                <Icon className="w-4 h-4" />
                {t.label}
              </button>
            );
          })}
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3 mb-6 p-4 bg-white border border-gray-200 rounded-lg">
          <div className="flex items-center gap-2">
            <label className="text-xs uppercase tracking-wider text-gray-500 font-medium">Scope</label>
            <select
              value={scope}
              onChange={(e) => setScope(e.target.value)}
              className="text-sm border border-gray-300 rounded-md px-3 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-[#00457f] min-w-[220px]"
              data-testid="scope-select"
            >
              <option value="global">All hubs (global)</option>
              {hubs.map((h) => (
                <option key={h.slug} value={h.slug}>{h.title || h.slug}  —  /{h.slug}</option>
              ))}
            </select>
          </div>

          {tab === 'overview' && (
            <div className="flex items-center gap-2">
              <label className="text-xs uppercase tracking-wider text-gray-500 font-medium">Range</label>
              <div className="flex bg-gray-100 rounded-md p-0.5" data-testid="range-picker">
                {RANGE_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => setDays(opt.value)}
                    className={`text-sm px-3 py-1 rounded transition-colors ${
                      days === opt.value ? 'bg-white shadow-sm text-[#00457f] font-medium' : 'text-gray-600 hover:text-gray-900'
                    }`}
                    data-testid={`range-${opt.value}-btn`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {tab !== 'overview' && (
            <div className="flex items-center gap-2 flex-1 min-w-[220px]">
              <div className="relative flex-1 max-w-md">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={captureSearch}
                  onChange={(e) => setCaptureSearch(e.target.value)}
                  placeholder="Search email, name, link…"
                  className="w-full pl-9 pr-8 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#00457f]"
                  data-testid="captures-search-input"
                />
                {captureSearch && (
                  <button
                    onClick={() => setCaptureSearch('')}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700"
                    aria-label="Clear search"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            </div>
          )}

          {scope !== 'global' && data?.hub && tab === 'overview' && (
            <div className="ml-auto flex items-center gap-2 text-sm">
              <a
                href={`/links/${scope}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#00457f] hover:underline flex items-center gap-1"
                data-testid="open-public-hub-link"
              >
                <ExternalLink className="w-3.5 h-3.5" />
                View /{scope}
              </a>
            </div>
          )}
        </div>

        {tab === 'overview' && (loading && !data ? (
          <div className="flex items-center justify-center py-24">
            <Loader2 className="w-6 h-6 text-[#00457f] animate-spin" />
          </div>
        ) : (
          <>
            {/* KPI Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6" data-testid="kpi-row">
              <StatCard
                icon={MousePointerClick}
                label="Total clicks"
                value={totalClicks.toLocaleString()}
                sub={days > 0 ? `Last ${days} days` : 'All time'}
                testId="kpi-total-clicks"
              />
              <StatCard
                icon={TrendingUp}
                label="Avg per day"
                value={avgPerDay.toLocaleString()}
                sub={timeline.length > 0 ? `Across ${timeline.length} day${timeline.length === 1 ? '' : 's'}` : 'No data yet'}
                testId="kpi-avg-per-day"
              />
              <StatCard
                icon={GlobeIcon}
                label={scope === 'global' ? 'Active hubs' : 'Unique links clicked'}
                value={scope === 'global' ? topHubs.length : uniqueLinks}
                sub={scope === 'global' ? 'with 1+ clicks in range' : 'distinct links'}
                testId="kpi-active-hubs"
              />
              <StatCard
                icon={Users}
                label="Unique referrers"
                value={uniqueReferrers}
                sub="including direct"
                testId="kpi-unique-referrers"
              />
            </div>

            {/* Timeline */}
            <div className="mb-6">
              <TimelineChart data={timeline} testId="click-timeline" />
            </div>

            {/* Tables */}
            <div className={`grid gap-6 mb-6 ${scope === 'global' ? 'lg:grid-cols-2' : 'lg:grid-cols-2'}`}>
              <RankedTable
                title="Top Links"
                rows={topLinks}
                testId="top-links-table"
                emptyText="No link clicks in this range yet."
                columns={[
                  {
                    key: 'label',
                    label: 'Link',
                    render: (r) => (
                      <div>
                        <div className="font-medium text-gray-900 truncate max-w-[260px]" title={r.label}>{r.label}</div>
                        {scope === 'global' && r.hub_title && (
                          <div className="text-xs text-gray-500">on <span className="font-medium">{r.hub_title}</span></div>
                        )}
                      </div>
                    ),
                  },
                  {
                    key: 'clicks',
                    label: 'Clicks',
                    className: 'text-right w-24',
                    render: (r) => <span className="font-mono font-semibold text-gray-900">{r.clicks}</span>,
                  },
                ]}
              />

              {scope === 'global' ? (
                <RankedTable
                  title="Top Hubs"
                  rows={topHubs}
                  testId="top-hubs-table"
                  emptyText="No hub activity yet."
                  columns={[
                    {
                      key: 'title',
                      label: 'Hub',
                      render: (r) => (
                        <div>
                          <div className="font-medium text-gray-900">{r.title || r.hub_slug}</div>
                          <a href={`/links/${r.hub_slug}`} target="_blank" rel="noopener noreferrer" className="text-xs text-[#00457f] hover:underline">/links/{r.hub_slug}</a>
                        </div>
                      ),
                    },
                    {
                      key: 'clicks',
                      label: 'Clicks',
                      className: 'text-right w-24',
                      render: (r) => <span className="font-mono font-semibold text-gray-900">{r.clicks}</span>,
                    },
                    {
                      key: '_view',
                      label: '',
                      className: 'text-right w-20',
                      render: (r) => (
                        <button
                          onClick={() => setScope(r.hub_slug)}
                          className="text-xs text-[#00457f] hover:underline"
                          data-testid={`drill-into-${r.hub_slug}-btn`}
                        >
                          Drill in →
                        </button>
                      ),
                    },
                  ]}
                />
              ) : (
                <RankedTable
                  title="Top Referrers"
                  rows={topReferrers}
                  testId="top-referrers-table"
                  emptyText="No referrer data yet."
                  columns={[
                    {
                      key: 'referrer',
                      label: 'Source',
                      render: (r) => (
                        <span className="font-medium text-gray-900">
                          {r.referrer === 'Direct' ? <span className="text-gray-500 italic">Direct / typed URL</span> : r.referrer}
                        </span>
                      ),
                    },
                    {
                      key: 'clicks',
                      label: 'Clicks',
                      className: 'text-right w-24',
                      render: (r) => <span className="font-mono font-semibold text-gray-900">{r.clicks}</span>,
                    },
                  ]}
                />
              )}
            </div>

            {scope === 'global' && (
              <div className="mb-6">
                <RankedTable
                  title="Top Referrers (global)"
                  rows={topReferrers}
                  testId="top-referrers-table"
                  emptyText="No referrer data yet."
                  columns={[
                    {
                      key: 'referrer',
                      label: 'Source',
                      render: (r) => (
                        <span className="font-medium text-gray-900">
                          {r.referrer === 'Direct' ? <span className="text-gray-500 italic">Direct / typed URL</span> : r.referrer}
                        </span>
                      ),
                    },
                    {
                      key: 'clicks',
                      label: 'Clicks',
                      className: 'text-right w-24',
                      render: (r) => <span className="font-mono font-semibold text-gray-900">{r.clicks}</span>,
                    },
                  ]}
                />
              </div>
            )}

            {/* Recent activity */}
            <Card className="border-gray-200" data-testid="recent-activity-card">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-semibold text-gray-900">Recent Activity</CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                {recent.length === 0 ? (
                  <p className="text-sm text-gray-400 py-6 text-center">No recent clicks in this range.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-xs uppercase tracking-wider text-gray-500 border-b">
                          <th className="text-left font-medium py-2 pr-3">When</th>
                          <th className="text-left font-medium py-2 pr-3">Link</th>
                          <th className="text-left font-medium py-2 pr-3">Hub</th>
                          <th className="text-left font-medium py-2 pr-3">Referrer</th>
                        </tr>
                      </thead>
                      <tbody>
                        {recent.map((c) => (
                          <tr key={c.id} className="border-b last:border-b-0 hover:bg-gray-50">
                            <td className="py-2.5 pr-3 text-gray-600 whitespace-nowrap">{fmtDateTime(c.timestamp)}</td>
                            <td className="py-2.5 pr-3 font-medium text-gray-900 truncate max-w-[240px]" title={c.link_label}>{c.link_label || '—'}</td>
                            <td className="py-2.5 pr-3 text-gray-600">/{c.hub_slug}</td>
                            <td className="py-2.5 pr-3 text-gray-600 truncate max-w-[200px]" title={c.referrer}>
                              {c.referrer ? c.referrer : <span className="italic text-gray-400">Direct</span>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        ))}

        {tab !== 'overview' && (
          <CapturesPanel
            kind={tab}
            data={tab === 'unlocks' ? unlocksData : leadsData}
            loading={captureLoading}
            onRefresh={fetchCaptures}
            onDownloadCsv={downloadCsv}
            contactLists={contactLists}
            bulkListId={bulkListId}
            setBulkListId={setBulkListId}
            bulkAddToList={bulkAddToList}
            bulkBusy={bulkBusy}
            scope={scope}
          />
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminLinkHubAnalytics;
